import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { keys as incidentKeys } from 'components/IAP/hooks/incidentHooks';
import { keys as incidentInfoKeys } from 'components/IAP/hooks/incidentInfoHooks';
import { incidentRosterKeys } from 'components/IAP/hooks/incidentRoster/incidentRoster.queryKeys';
import type { IncidentRoster } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import apiConfig from 'constants/apiConfig';
import { useHwcgFlag } from 'utils/featureFlags';

/**
 * A scheduled operational period after the current one. Advancing turns the
 * earliest into the new current period, along with its roster.
 */
export type PlannedOperationalPeriod = {
  id: string;
  incidentId: string;
  periodNumber: number;
  startDateTime: string | null;
  anticipatedEndDateTime: string | null;
  rosterId: string | null;
};

/** The current operational period's number and window. */
export type CurrentOperationalPeriodWindow = {
  periodNumber: number;
  startDateTime: string | null;
  anticipatedEndDateTime: string | null;
};

export type PlannedOperationalPeriodsView = {
  /** 0 while the incident has no operational period. */
  currentPeriodNumber: number;
  /** Null while the incident has no operational period. */
  current?: CurrentOperationalPeriodWindow | null;
  periods: PlannedOperationalPeriod[];
  /** Advances to each planned period at its start (HWCG). */
  autoAdvance?: boolean;
};

export type PlannedWindowInput = {
  id?: string | null;
  startDateTime: string | null;
  anticipatedEndDateTime: string | null;
};

export const plannedOperationalPeriodKeys = {
  base: ['plannedOperationalPeriods'] as const,
  byIncident: (incidentId: string) =>
    [...plannedOperationalPeriodKeys.base, incidentId] as const,
  membersBase: ['plannedOperationalPeriodMembers'] as const,
  members: (incidentId: string, plannedPeriodId: string) =>
    [
      ...plannedOperationalPeriodKeys.membersBase,
      incidentId,
      plannedPeriodId,
    ] as const,
};

const EMPTY_VIEW: PlannedOperationalPeriodsView = {
  currentPeriodNumber: 0,
  current: null,
  periods: [],
};

export function usePlannedOperationalPeriods(
  incidentId?: string | null,
  enabled = true
) {
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId || actualIncidentId || '';
  const query = useQuery({
    queryKey: plannedOperationalPeriodKeys.byIncident(resolvedIncidentId),
    enabled: Boolean(resolvedIncidentId) && enabled,
    queryFn: async () => {
      const response = await axios.get<PlannedOperationalPeriodsView>(
        apiConfig.apiGateway.plannedOperationalPeriods(resolvedIncidentId)
      );
      return response.data;
    },
  });
  return { ...query, view: query.data ?? EMPTY_VIEW };
}

/** Everything that reads a planned window or a planned roster. */
export function invalidatePlannedPeriodQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  incidentId: string
) {
  void queryClient.invalidateQueries({
    queryKey: plannedOperationalPeriodKeys.byIncident(incidentId),
  });
  void queryClient.invalidateQueries({
    queryKey: plannedOperationalPeriodKeys.membersBase,
  });
  void queryClient.invalidateQueries({
    queryKey: incidentInfoKeys.get(incidentId as UUID),
  });
  void queryClient.invalidateQueries({
    queryKey: incidentKeys.periods(incidentId as UUID),
  });
  void queryClient.invalidateQueries({
    queryKey: incidentRosterKeys.draftByIncident(incidentId),
  });
}

/**
 * Saves the planned periods in order. Orgs with multi-period scheduling can
 * keep several; everyone else keeps only the next period.
 */
export function useUpsertPlannedOperationalPeriods(incidentId?: string | null) {
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId || actualIncidentId || '';
  const multiPeriodScheduling = useHwcgFlag();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (periods: PlannedWindowInput[]) => {
      const response = await axios.put<PlannedOperationalPeriodsView>(
        apiConfig.apiGateway.plannedOperationalPeriods(resolvedIncidentId),
        { periods, multiPeriodScheduling }
      );
      return response.data;
    },
    onSuccess: () =>
      invalidatePlannedPeriodQueries(queryClient, resolvedIncidentId),
    onError: (error) => {
      const message = axios.isAxiosError<{ error?: { message?: string } }>(
        error
      )
        ? error.response?.data?.error?.message
        : undefined;
      toast.error(message || 'The operational periods could not be saved.');
    },
  });
}

/**
 * Turns scheduled auto-advance on or off; scheduled advances act as whoever
 * turned it on.
 */
export function useSaveOperationalAutoAdvance(incidentId?: string | null) {
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId || actualIncidentId || '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (autoAdvance: boolean) => {
      const response = await axios.put<{ autoAdvance: boolean }>(
        apiConfig.apiGateway.operationalScheduleSettings(resolvedIncidentId),
        { autoAdvance }
      );
      return response.data;
    },
    onSuccess: () =>
      invalidatePlannedPeriodQueries(queryClient, resolvedIncidentId),
    onError: () => {
      toast.error('The auto-advance setting could not be saved.');
    },
  });
}

/**
 * Edits only the next period's window, keeping any later planned periods.
 * Creates the next planned period when none exists yet.
 */
export function useSaveNextPlannedWindow(incidentId?: string | null) {
  const planned = usePlannedOperationalPeriods(incidentId);
  const upsert = useUpsertPlannedOperationalPeriods(incidentId);
  const [next, ...later] = planned.view.periods;
  const save = (window: { startIso: string | null; endIso: string | null }) =>
    upsert.mutateAsync([
      {
        id: next?.id ?? null,
        startDateTime: window.startIso,
        anticipatedEndDateTime: window.endIso,
      },
      ...later.map(toPlannedWindowInput),
    ]);
  return {
    save,
    next: next ?? null,
    isReady: planned.isSuccess,
    isPending: upsert.isPending,
  };
}

export function toPlannedWindowInput(
  period: PlannedOperationalPeriod
): PlannedWindowInput {
  return {
    id: period.id,
    startDateTime: period.startDateTime,
    anticipatedEndDateTime: period.anticipatedEndDateTime,
  };
}

async function fetchPlannedPeriodMembers(
  incidentId: string,
  plannedPeriodId: string
): Promise<IncidentRoster> {
  const response = await axios.get<IncidentRoster>(
    apiConfig.apiGateway.plannedOperationalPeriodMembers(
      incidentId,
      plannedPeriodId
    )
  );
  return response.data ?? [];
}

/** Each planned period's roster, keyed by planned period id. */
export function usePlannedPeriodRosters(
  incidentId: string | null | undefined,
  periods: readonly PlannedOperationalPeriod[],
  enabled = true
): { membersByPeriodId: Record<string, IncidentRoster>; isLoading: boolean } {
  const periodIds = periods
    .filter((period) => period.rosterId)
    .map((period) => period.id);
  const periodIdsKey = periodIds.join(',');
  const combine = (results: UseQueryResult<IncidentRoster>[]) => {
    const ids = periodIdsKey ? periodIdsKey.split(',') : [];
    const membersByPeriodId: Record<string, IncidentRoster> = {};
    ids.forEach((id, index) => {
      membersByPeriodId[id] = results[index]?.data ?? [];
    });
    return {
      membersByPeriodId,
      isLoading: results.some((result) => result.isLoading),
    };
  };
  return useQueries({
    queries: periodIds.map((id) => ({
      queryKey: plannedOperationalPeriodKeys.members(incidentId ?? '', id),
      queryFn: () => fetchPlannedPeriodMembers(incidentId ?? '', id),
      enabled: Boolean(incidentId) && enabled,
    })),
    combine,
  });
}
